import type {
  BillingReadiness,
  PixAutomaticoErrorCode,
  PixAutomaticoFailure,
  PixEnrollment,
  WeeklyLimit,
} from "../../services/pixAutomatico";

/**
 * What the Pix Automatico panel shows. Every member is a screen the company
 * can actually land on, and each one is a story in the component lab.
 */
export type PixAutomaticoView =
  | { kind: "loading" }
  | { kind: "load_error"; message: string }
  | { kind: "activation_disabled" }
  | { kind: "not_enrolled" }
  | {
      kind: "profile_incomplete";
      /** Profile fields only the company can fill in (Enterprise ADR 0009 names). */
      missing?: string[];
      /** The company registry could not be read; a later retry may pass. */
      registryUnavailable?: boolean;
    }
  | { kind: "preparing" }
  | { kind: "waiting"; pixCopyAndPaste: string }
  | { kind: "confirming" }
  | { kind: "reconciling" }
  | { kind: "approved"; observedAt?: string }
  | { kind: "approved_unconfirmed"; observedAt?: string }
  | { kind: "cancel_requested" }
  | { kind: "rejected" }
  | { kind: "expired" }
  | { kind: "cancelled" }
  | { kind: "reviewed_retry" }
  | { kind: "blocked" };

export type PixAutomaticoViewKind = PixAutomaticoView["kind"];

/** The company-owned weekly limit as Payments stores it. */
export type StoredWeeklyLimit = WeeklyLimit & { version: number };

export type PixAutomaticoNotice = {
  tone: "error" | "warning" | "info" | "success";
  message: string;
};

export type PixAutomaticoAction = "start" | "cancel" | "limit";

/**
 * What Payments' readiness adds to the enrollment itself: the enrollment says
 * where the authorization is, readiness says whether Payments counts it.
 */
export type ReadinessContext = {
  /**
   * Payments accepts new authorizations. An existing one stays readable and
   * cancellable while it is off (Payments records a cancellation either way).
   */
  activationEnabled: boolean;
  /**
   * Payments counts the approved authorization now: APROVADA, not being
   * cancelled, on its current Efi account and observed within its freshness
   * window (StaleReadinessLimit). An approved phase without it is shown as
   * waiting for confirmation, never as authorized.
   */
  authorizationReady: boolean;
  /**
   * Payments' weekly admission is active, the only place the weekly limit
   * is enforced. While Payments reports `exposure_admission_not_activated`
   * the limit is only stored: nothing limits or pauses a campaign with it,
   * and cancelling pauses nothing either, so the copy must not suggest it.
   */
  weeklyAdmissionActive: boolean;
};

export const EXPOSURE_ADMISSION_NOT_ACTIVATED = "exposure_admission_not_activated";

export function readinessContext(readiness: BillingReadiness): ReadinessContext {
  return {
    activationEnabled: readiness.activationEnabled,
    authorizationReady: readiness.authorizationReady,
    weeklyAdmissionActive: !readiness.reasons.includes(EXPOSURE_ADMISSION_NOT_ACTIVATED),
  };
}

/**
 * Maps the Payments phase, read together with its readiness, to a screen. A
 * phase not listed is shown as "confirming".
 */
export function viewForEnrollment(
  enrollment: PixEnrollment | null,
  context: ReadinessContext,
): PixAutomaticoView {
  // Without an enrollment the only thing to offer is a start, which Payments
  // refuses while the activation is off.
  if (!enrollment) {
    return context.activationEnabled ? { kind: "not_enrolled" } : { kind: "activation_disabled" };
  }
  switch (enrollment.state) {
    case "pending_provider":
      return { kind: "preparing" };
    case "waiting_authorization":
      // CRIADA without a Journey 2 payload has nothing to scan yet.
      return enrollment.pixCopyAndPaste
        ? { kind: "waiting", pixCopyAndPaste: enrollment.pixCopyAndPaste }
        : { kind: "preparing" };
    case "approved":
      return context.authorizationReady
        ? { kind: "approved", observedAt: enrollment.observedAt }
        : { kind: "approved_unconfirmed", observedAt: enrollment.observedAt };
    case "cancel_requested":
      return { kind: "cancel_requested" };
    case "rejected":
      return { kind: "rejected" };
    case "expired":
      return { kind: "expired" };
    case "cancelled":
    case "stopped":
      return { kind: "cancelled" };
    case "blocked":
      return enrollment.resolution === "operator_rejected_unknown_recurrence"
        ? { kind: "reviewed_retry" }
        : { kind: "blocked" };
    case "unknown":
      return { kind: "reconciling" };
    default:
      return { kind: "confirming" };
  }
}

/**
 * Screens whose outcome is still being decided at the provider. The panel
 * polls GET /pix-automatico/current only while it shows one of these, and
 * stops as soon as it reaches any other screen.
 */
const MOVING: ReadonlySet<PixAutomaticoViewKind> = new Set<PixAutomaticoViewKind>([
  "preparing",
  "waiting",
  "confirming",
  "reconciling",
  "cancel_requested",
]);

export function shouldPoll(view: PixAutomaticoView): boolean {
  return MOVING.has(view.kind);
}

/** Screens where the company may start a new authorization. */
const STARTABLE: ReadonlySet<PixAutomaticoViewKind> = new Set<PixAutomaticoViewKind>([
  "not_enrolled",
  "rejected",
  "expired",
  "cancelled",
  "reviewed_retry",
]);

export function canStartFrom(view: PixAutomaticoView): boolean {
  return STARTABLE.has(view.kind);
}

/**
 * Payments observes the provider about once a minute and on each webhook, so
 * five seconds is responsive without spending the route's 60 per minute
 * budget. Failures back off up to a minute and never stop the page by
 * themselves; leaving the page does.
 */
export const POLL_INTERVAL_MS = 5_000;
export const MAX_POLL_INTERVAL_MS = 60_000;

export function nextPollDelay(consecutiveFailures: number): number {
  const exponent = Math.max(0, Math.min(consecutiveFailures, 8));
  return Math.min(POLL_INTERVAL_MS * 2 ** exponent, MAX_POLL_INTERVAL_MS);
}

/** Failures a later poll can recover from on its own. */
const TRANSIENT: ReadonlySet<PixAutomaticoErrorCode> = new Set<PixAutomaticoErrorCode>([
  "network",
  "billing_unavailable",
  "rate_limited",
]);

export function isTransientFailure(code: PixAutomaticoErrorCode): boolean {
  return TRANSIENT.has(code);
}

/**
 * Failures after which a create or cancel may or may not have happened.
 * Payments still owns the answer, so the panel re-reads it instead of guessing.
 */
const UNCERTAIN: ReadonlySet<PixAutomaticoErrorCode> = new Set<PixAutomaticoErrorCode>([
  "network",
  "billing_unavailable",
  "rate_limited",
  "malformed_response",
  "unexpected",
]);

export function isUncertainOutcome(code: PixAutomaticoErrorCode): boolean {
  return UNCERTAIN.has(code);
}

export function weeklyLimitFromReadiness(readiness: BillingReadiness): StoredWeeklyLimit | null {
  if (!readiness.weeklyLimit || readiness.weeklyLimitVersion === 0) return null;
  return { ...readiness.weeklyLimit, version: readiness.weeklyLimitVersion };
}

/* ── Copy ─────────────────────────────────────────────────────────────── */

const SESSION_EXPIRED = "Sua sessão expirou. Entre novamente para continuar.";
const CONNECTION = "Não foi possível conectar ao DaKasa. Confira sua conexão e tente de novo.";

/** Why the panel could not load. Never echoes anything from the response. */
export function loadErrorMessage(failure: PixAutomaticoFailure): string {
  switch (failure.code) {
    case "unauthorized":
      return SESSION_EXPIRED;
    case "forbidden":
      return "Seu perfil não tem permissão para ver a cobrança da organização. Fale com um administrador da empresa.";
    case "route_missing":
      return "O sistema de cobrança ainda não oferece o Pix Automático neste ambiente. Fale com o suporte.";
    case "network":
      return CONNECTION;
    case "rate_limited":
      return "Recebemos muitas consultas seguidas. Aguarde um instante e tente de novo.";
    default:
      return "Não foi possível carregar a cobrança semanal agora. Tente de novo em instantes.";
  }
}

export function loadErrorView(failure: PixAutomaticoFailure): PixAutomaticoView {
  return { kind: "load_error", message: loadErrorMessage(failure) };
}

const FORBIDDEN: Record<PixAutomaticoAction, string> = {
  start:
    "Seu perfil não tem permissão para autorizar o Pix Automático. Fale com um administrador da empresa.",
  cancel:
    "Seu perfil não tem permissão para cancelar a autorização. Fale com um administrador da empresa.",
  limit:
    "Seu perfil não tem permissão para alterar o limite semanal. Fale com um administrador da empresa.",
};

const UNCERTAIN_COPY: Record<PixAutomaticoAction, string> = {
  start:
    "Não conseguimos confirmar se o pedido chegou. Confira o estado abaixo; se precisar tentar de novo, o mesmo pedido não cria uma segunda autorização.",
  cancel:
    "Não conseguimos confirmar o cancelamento. Confira o estado abaixo antes de tentar de novo.",
  limit: "Não foi possível salvar o limite semanal agora. Tente de novo em instantes.",
};

/** Notice after a failed action. The state-changing codes are handled by the controller. */
export function actionFailureNotice(
  action: PixAutomaticoAction,
  failure: PixAutomaticoFailure,
): PixAutomaticoNotice {
  switch (failure.code) {
    case "confirmed_spend_outstanding":
      return { tone: "warning", message: CANCEL_OUTSTANDING_SPEND };
    case "unauthorized":
      return { tone: "error", message: SESSION_EXPIRED };
    case "forbidden":
      return { tone: "error", message: FORBIDDEN[action] };
    case "route_missing":
      return {
        tone: "error",
        message:
          "O sistema de cobrança ainda não oferece o Pix Automático neste ambiente. Fale com o suporte.",
      };
    case "invalid_request":
      return {
        tone: "error",
        message:
          action === "limit"
            ? "Escolha um limite semanal válido e tente de novo."
            : "A DaKasa recusou os dados enviados. Confira o limite semanal e tente de novo; se continuar, fale com o suporte.",
      };
    case "state_conflict":
    case "not_found":
      return {
        tone: "warning",
        message: "A autorização mudou enquanto você agia. Atualizamos a tela com o estado atual.",
      };
    default:
      return { tone: "warning", message: UNCERTAIN_COPY[action] };
  }
}

/**
 * Copy of the start the panel offers only on a reviewed processing fee. The
 * controller repeats the rule, so a start can never go out without the fee
 * the company was shown.
 */
export const START_WAITS_FOR_FEE =
  "A autorização fica disponível aqui quando a tarifa de processamento Pix de cada cobrança semanal estiver definida.";

export const CANCEL_OUTSTANDING_SPEND =
  "Há gastos confirmados ainda não quitados, inclusive de semanas anteriores. O cancelamento pela DaKasa fica disponível após a quitação desses gastos.";
export const CANCEL_ELIGIBILITY_UNKNOWN =
  "Não conseguimos confirmar agora se há gastos pendentes. Verifique de novo antes de pedir o cancelamento pela DaKasa.";

export const WEEKLY_LIMIT_CONFLICT_RELOADED =
  "Outra pessoa alterou o limite semanal enquanto você editava. Carregamos a escolha atual: revise e salve de novo.";
export const WEEKLY_LIMIT_CONFLICT_STALE =
  "Outra pessoa alterou o limite semanal enquanto você editava. Recarregue a página para ver a escolha atual antes de salvar de novo.";

/* ── Money ────────────────────────────────────────────────────────────── */

/** Initial UI choice only; Payments still requires the explicit object. */
export const DEFAULT_FIXED_WEEKLY_LIMIT_CENTS = 2_500_000;

/** Exact fixed-amount bound enforced by the Payments request contract. */
export const MAX_WEEKLY_LIMIT_CENTS = 999_999_999_999;

/**
 * Reads a pt-BR amount ("1.500", "1.500,50", "R$ 250,00") as integer cents.
 * Integer arithmetic only: a float round trip could shift a cent. Returns null
 * for anything ambiguous ("1.5"), zero, negative or out of range.
 */
export function parseReaisToCents(raw: string): number | null {
  const text = raw.replace(/\s/g, "").replace(/^R\$/i, "");
  if (!text) return null;
  let integer = text;
  let fraction = "";
  const comma = text.lastIndexOf(",");
  if (comma >= 0) {
    integer = text.slice(0, comma);
    fraction = text.slice(comma + 1);
    if (!/^\d{1,2}$/.test(fraction)) return null;
  }
  if (/^\d{1,3}(\.\d{3})+$/.test(integer)) integer = integer.replace(/\./g, "");
  if (!/^\d+$/.test(integer) || integer.length > 12) return null;
  const cents = Number(integer) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(cents) || cents <= 0 || cents > MAX_WEEKLY_LIMIT_CENTS) return null;
  return cents;
}

/** "1.500,00", the editable form of an amount. */
export function centsToReaisInput(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
